import type { DirectiveBinding } from 'vue'
import type QuillType from 'quill'
import { defineNuxtPlugin, useState } from 'nuxt/app'
import type { StyleAttributor } from 'parchment'
import { useAuthStore } from '../stores/authStore'
import { useCmsEditStore } from '../stores/cmsEditStore'
import type { CmsData } from '../types/cmsTypes'
import { setByPath } from '#imports'

let Quill: typeof QuillType | null = null

interface CmsHandlerEvent {
  event: EventListener | ((e: KeyboardEvent) => void)
  event_type: keyof WindowEventMap
  target: HTMLElement | Window
}

interface CmsElement extends HTMLElement {
  __cmsHandlers: CmsHandlerEvent[]
  _cmsPath: string[]
  _global: boolean
  __cmsLabel?: HTMLElement
}
const blurHandlers = new WeakMap<CmsElement, () => void>()

const mountedElements = new Set<CmsElement>()
const quillInstances = new WeakMap<CmsElement, QuillType>()

const TOOLBAR_OPTIONS = [
  ['bold', 'italic', 'underline', 'strike'],
  ['blockquote', 'code-block'],
  ['link', 'image', 'video', 'formula'],
  [{ header: 1 }, { header: 2 }],
  [{ list: 'ordered' }, { list: 'bullet' }, { list: 'check' }],
  [{ script: 'sub' }, { script: 'super' }],
  [{ indent: '-1' }, { indent: '+1' }],
  [{ direction: 'rtl' }],
  [{ size: ['18px', '22px', '30px', '50px', '80px'] }],
  [{ header: [1, 2, 3, 4, 5, 6, false] }],
  [
    { color: ['#AF404E', '#97A884', '#B8C1A8', '#455049', '#F4F6F3', '#E5EBDF', '#E5EBDF', '#E1DEF2', '#DFC7CA', '#DCE29A', false] },
    { background: ['#AF404E', '#97A884', '#B8C1A8', '#455049', '#F4F6F3', '#E5EBDF', '#E5EBDF', '#E1DEF2', '#DFC7CA', '#DCE29A', false] }
  ],
  [{ align: [] }],
  ['clean']
]

function decodeHtml(value: string): string {
  return value.replace(/&lt;/g, '<').replace(/&gt;/g, '>')
}

function removeQuill(el: CmsElement): void {
  const quill = quillInstances.get(el)
  if (!quill) return
  quill.disable()
  const html = quill.root ? quill.root.innerHTML : el.innerHTML

  const blurHandler = blurHandlers.get(el)
  if (blurHandler) {
    quill.root.removeEventListener('blur', blurHandler)
    blurHandlers.delete(el)
  }

  el.querySelectorAll('.ql-toolbar, .ql-tooltip, .ql-clipboard').forEach(n => n.remove())
  el.classList.remove('ql-container', 'ql-bubble')
  el.removeAttribute('data-quill')
  el.innerHTML = html
  quillInstances.delete(el)
}

function removeOtherQuills(except: CmsElement): void {
  mountedElements.forEach((el) => {
    if (el !== except) removeQuill(el)
  })
}

function makeEditable(el: CmsElement): () => void {
  return async () => {
    removeOtherQuills(el)
    if (quillInstances.has(el) || !Quill) return

    const quill = new Quill(el, { modules: { toolbar: TOOLBAR_OPTIONS }, theme: 'bubble' })
    quillInstances.set(el, quill)

    const editStore = useCmsEditStore()
    const cmsData = useState<CmsData>('cmsData')

    quill.on('text-change', () => {
      editStore.set(el._cmsPath, {
        component_type: 'CmsText',
        component_data: { value: decodeHtml(quill.root.innerHTML) }
      }, el._global)
    })

    const blurHandler = () => {
      setByPath(cmsData.value, el._cmsPath, {
        component_type: 'CmsText',
        component_data: { value: decodeHtml(quill.root.innerHTML) }
      })
    }
    blurHandlers.set(el, blurHandler)
    quill.root.addEventListener('blur', blurHandler)
  }
}

export default defineNuxtPlugin((nuxtApp) => {
  nuxtApp.vueApp.directive('cms-text-element', {
    async mounted(el: CmsElement, binding: DirectiveBinding) {
      const authStore = useAuthStore()
      if (!authStore.authorized) return

      if (!binding.value) {
        el.style.border = '2px solid red'
        el.title = 'Brak wartości cms-value!'
        return
      }

      const editStore = useCmsEditStore()
      const { path, global = false } = binding.value as {
        path: string[]
        global: boolean
      }

      el._cmsPath = path
      el.__cmsHandlers = []
      el._global = global

      editStore.set(path, {
        component_type: 'CmsText',
        component_data: { value: decodeHtml(el.innerHTML) }
      }, global)

      if (Quill === null) {
        const { default: quill } = await import('quill')
        Quill = quill

        const Size = Quill.import('attributors/style/size') as StyleAttributor
        Size.whitelist = ['18px', '22px', '30px', '50px', '80px']
        Quill.register(Size, true)
      }

      const mouseEnterHandler = () => {
        el.style.outline = '2px solid blue'
      }
      const mouseLeaveHandler = () => {
        el.style.outline = ''
      }
      el.__cmsHandlers.push({ target: el, event: mouseEnterHandler as EventListener, event_type: 'mouseenter' })
      el.__cmsHandlers.push({ target: el, event: mouseLeaveHandler as EventListener, event_type: 'mouseleave' })
      el.addEventListener('mouseenter', mouseEnterHandler)
      el.addEventListener('mouseleave', mouseLeaveHandler)

      const clickHandler = makeEditable(el)
      el.__cmsHandlers.push({ event: clickHandler as EventListener, event_type: 'click', target: el })
      el.addEventListener('click', clickHandler)

      const label = document.createElement('div')
      label.textContent = 'Editable text'
      label.style.cssText = 'position:fixed;padding:2px 6px;font-size:10px;background:rgba(0,0,0,.7);color:#fff;pointer-events:none;z-index:9999'
      document.body.appendChild(label)
      label.style.display = 'none'
      el.__cmsLabel = label

      const positionLabel = () => {
        const rect = el.getBoundingClientRect()
        label.style.top = `${rect.top}px`
        label.style.left = `${rect.left}px`
      }

      const show = (e: KeyboardEvent) => {
        if (!e.shiftKey) return
        positionLabel()
        label.style.display = 'block'
        el.style.outline = '2px solid blue'
      }
      const hide = () => {
        label.style.display = 'none'
        el.style.outline = ''
      }
      window.addEventListener('scroll', positionLabel, true)
      window.addEventListener('resize', positionLabel)
      el.__cmsHandlers.push({ target: window, event: positionLabel, event_type: 'scroll' })
      el.__cmsHandlers.push({ target: window, event: positionLabel, event_type: 'resize' })
      el.__cmsHandlers.push({ event: show as EventListener, event_type: 'keydown', target: window })
      el.__cmsHandlers.push({ event: hide as EventListener, event_type: 'keyup', target: window })
      window.addEventListener('keydown', show)
      window.addEventListener('keyup', hide)
      mountedElements.add(el)
    },

    // updated(el: CmsElement, binding: DirectiveBinding) {
    //   //if (!binding.arg) return
    //   console.log(el, binding)
    //   const editStore = useCmsEditStore()
    //   const cmsData = useState<CmsData>('cmsData')
    //   console.log(editStore.getAll())
    //   console.log(cmsData.value)

    //   // const editStore = useCmsEditStore()
    //   // console.log(
    //   //   el._cmsPath,
    //   //   editStore.getAll()
    //   // )
    //   // editStore.set(el._cmsPath, { component_type: 'CmsText', component_data: binding.arg as CmsText }, el._global)
    //   // console.log(
    //   //   el._cmsPath,
    //   //   editStore.getAll()
    //   // )

    // },

    beforeUnmount(el: CmsElement) {
      removeQuill(el)
      for (const h of el.__cmsHandlers ?? []) {
        h.target.removeEventListener(h.event_type, h.event as EventListener)
      }
      el.__cmsHandlers = []
      if (el.__cmsLabel && el.__cmsLabel.parentNode) {
        el.__cmsLabel.parentNode.removeChild(el.__cmsLabel)
      }
      el.__cmsLabel = undefined
      mountedElements.delete(el)
    }
  })
})
